import type { RouteRecordRaw } from 'vue-router'
import type { Component } from 'vue'
import { MenuInfo } from '@/types/menu'
import { LanguageManager } from './language'

const Layout = () => import('@/layout/Layout.vue')

/** 记录页需要每次进入重新拉取数据，不纳入页面缓存。 */
const NON_CACHED_ROUTE_PATHS = new Set(['FaceSyncRecords'])



/**
 * 动态导入组件
 */
function loadComponent(componentPath: string): () => Promise<Component> {
  if (componentPath === 'Layout') return Layout

  // 使用 import.meta.glob 预加载所有组件
  const modules = import.meta.glob('../pages/**/*.vue')

  // 构造完整的组件路径
  const fullPath = `/src/pages${componentPath}.vue`

  // console.log('Trying to load component from:', fullPath)

  // console.log('Loading component path:', componentPath)
  // 尝试不同的路径格式
  const possiblePaths = [
    fullPath,
    `../pages${componentPath}.vue`,
    `/src/pages${componentPath}.vue`,
    componentPath
  ]

  for (const path of possiblePaths) {
    // console.log('Checking path:', path)

    if (modules[path]) {
      // console.log('Found component at:', path)

      return modules[path] as () => Promise<Component>
    }
  }

  return Layout // 临时返回Layout组件
}

/** 以菜单路由路径的末段作为路由名称，与既有页面跳转约定保持一致。 */
function generateRouteName(path: string): string {
  return path.split('/').filter(Boolean).pop() || ''
}

function getRoutePathSegment(path: string): string {
  return path.split('/').filter(Boolean).pop() || ''
}

/**
 * 处理路由路径
 */
function handleRoutePath(path: string, parentPath: string = ''): string {
  const normalizeAbsolutePath = (value: string) => {
    const normalizedPath = `/${value}`.replace(/\/+/g, '/')
    return normalizedPath === '/' ? normalizedPath : normalizedPath.replace(/\/$/, '')
  }
  const normalizedPath = normalizeAbsolutePath(path)

  if (parentPath) {
    const normalizedParentPath = normalizeAbsolutePath(parentPath)
    if (normalizedPath === normalizedParentPath) {
      return ''
    }
    if (normalizedPath.startsWith(`${normalizedParentPath}/`)) {
      return normalizedPath.slice(normalizedParentPath.length + 1)
    }

    return normalizedPath.replace(/^\/+/, '')
  }

  return normalizedPath
}

/**
 * 递归生成路由配置
 */
function generateRouteRecords(menus: MenuInfo[], parentPath: string = ''): RouteRecordRaw[] {
  const currentLanguage = LanguageManager.getCurrentLanguage()

  return menus.map(menu => {
    // 处理路由路径
    const path = handleRoutePath(menu.routePath, parentPath)

    // 处理 componentPath
    let component = null
    if (menu.componentPath) {
      // 过滤掉 "null" 和空格字符串
      const componentPath = menu.componentPath.trim()
      if (componentPath !== "null" && componentPath !== "") {
        component = loadComponent(componentPath)
      }
    }
    const rawName = (menu.menuName as string || '').trim()
    let labels = ''
    if (rawName.startsWith('{')) {
      try {
        const i18nContent = JSON.parse(rawName)
        switch (currentLanguage) {
          case 'zh_TW':
            labels = i18nContent['zh-MO'] || ''
            break
          case 'en_US':
            labels = i18nContent['en-US'] || ''
            break
          case 'pt_PT':
            labels = i18nContent['pt-PT'] || ''
            break
          default:
            labels = i18nContent['zh-MO'] || ''
        }
      } catch (e) {
        console.warn('Failed to parse i18n title:', e)
        labels = rawName
      }
    } else {
      labels = rawName
    }
    const routeName = generateRouteName(menu.routePath)
    const route: RouteRecordRaw = {
      path,
      name: routeName,
      component,
      meta: {
        title: labels,
        icon: menu.icon,
        hidden: menu.status !== 1,
        permission: menu.permission,
        keepAlive: !NON_CACHED_ROUTE_PATHS.has(getRoutePathSegment(menu.routePath)),
        breadcrumb: true
      }
    }

    // 处理子路由
    if (menu.children && menu.children.length > 0) {
      const showChildren = menu.children.filter(child => child.type !== 2)
      if (showChildren.length > 0) {
        route.children = generateRouteRecords(showChildren, menu.routePath)
      }
    }

    return route
  })
}

// 递归过滤菜单数据
const filterMenus = (menus: MenuInfo[]): MenuInfo[] => {
  return menus.map(menu => ({
    ...menu,
    children: menu.children
      ? menu.children.filter(child => child.type === 1 || child.type === 2)
      : []
  })).filter(menu => menu.type === 1)
}

/**
 * 将菜单数据转换为路由配置
 */
export function generateRoutesFromMenu(menuData: any): RouteRecordRaw[] {
  if (!menuData || !Array.isArray(menuData)) {
    return []
  }

  return generateRouteRecords(menuData)
}
